import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Radium } from 'higher-order-components'
import debounce from 'debounce'
import { InputText } from 'components'
import { getAutocompleteService } from 'apis/google-maps'
import { fetchGeoDataFromAddress } from 'apis/geo'
import { invokeFuncs } from 'utils/function'

class PostcodeSuggest extends Component {
  constructor(props) {
    super(props)
    this.state = { autocompleteItems: [] }
    this.autocompleteCallback = this.autocompleteCallback.bind(this)
    this.handleInputKeyDown = this.handleInputKeyDown.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.places = {}
  }
  static defaultProps = {
    componentRestrictions: { country: 'nz' }
  }
  async getAutocompleteService() {
    if (this.autocompleteService && this.autocompleteOK !== undefined) {
      return this.autocompleteService
    }

    try {
      const { autocompleteService, statusCodes } = await getAutocompleteService()

      this.autocompleteService = autocompleteService
      this.autocompleteOK = statusCodes.OK
      this.autocompleteZERO_RESULTS = statusCodes.ZERO_RESULTS

      return this.autocompleteService
    } catch (error) {
      throw error
    }
  }
  componentDidMount() {
    this.getSuggestionsDebounced = debounce(
      (value) => {
        if(!value) return;

        this.getAutocompleteService().then((service) => {
          service.getPlacePredictions(
            {
              input: value,
              componentRestrictions: this.props.componentRestrictions,
              types: ['(regions)']
            },
            this.autocompleteCallback
          )
        })
      },
      100,
      false
    )
  }
  autocompleteCallback(predictions, status) {
    if (status === this.autocompleteZERO_RESULTS) {
      this.setState({ autocompleteItems: [] })
      return
    }
    if (status !== this.autocompleteOK) return
    const predictionResults = predictions
      // Ensure that places without a postcode won't be returned
      .filter(({ types }) =>
        ['colloquial_area'].some(type => types.indexOf(type) === -1) &&
        ['postal_code', 'locality'].some(type => types.indexOf(type) !== -1))
      // Format, ready to put in state
      .map((place, index) => ({
        suggestion: place.description,
        placeId: place.place_id,
        active: false,
        index: index
      }))
    this.setState({
      autocompleteItems: predictionResults
    })
  }
  clearAutocomplete() {
    this.setState({ autocompleteItems: [] })
  }
  selectAddress(address, id) {
    this.clearAutocomplete()
    this._handleSelect(address, id)
  }
  _selectPlace(activeName, placeId) {
    const { onChange, onLocation } = this.props
    const handleGeoCode = (params, pid) => {
      const {
        postcode, state, suburb, lat, lng, number, street
      } = params
      this.places[pid] = {
        postcode, state, suburb, lat, lng, number, street
      }

      const address = `${number} ${street}`

      const name = activeName.indexOf(postcode) === -1 ? `${postcode}, ${activeName}` : activeName
      onChange({
        name, postcode, state, suburb, address
      })
      onLocation && onLocation({
        name, postcode, state, suburb, address
      })
      window.dispatchEvent(new CustomEvent('postcodeSelect', {
        detail: { label: this.props.placeholder, message: name }
      }))
      // Dispatch gtm events from here so all information fetched is captured
      this.props.gtmEvents && invokeFuncs(this.props.gtmEvents)
    }

    if (this.places[placeId]) {
      handleGeoCode(this.places[placeId], placeId)
    } else {
      fetchGeoDataFromAddress({
        placeId
      }).then(({ addressDetails, placeId: pid }) => handleGeoCode(addressDetails, pid))
    }
  }
  _handleSelect(address, id) {
    this._selectPlace(address, id)
  }
  _getActiveItem() {
    return this.state.autocompleteItems.find(item => item.active)
  }
  _selectActiveItemAtIndex(index) {
    const activeItem = this.state.autocompleteItems.find(item => item.index === index)
    const activeName = activeItem.suggestion
    this._setActiveItemAtIndex(index)
    this._selectPlace(activeName, activeItem.placeId)
  }
  _handleEnterKey() {
    let activeItem = this._getActiveItem()
    if (activeItem === undefined) {
      activeItem = this.state.autocompleteItems[0]
    }
    this.clearAutocomplete()
    if (activeItem !== undefined) {
      this._handleSelect(activeItem.suggestion, activeItem.placeId)
    }
  }
  _handleDownKey() {
    const activeItem = this._getActiveItem()
    if (activeItem === undefined) {
      this._selectActiveItemAtIndex(0)
    } else {
      const nextIndex = (activeItem.index + 1) % this.state.autocompleteItems.length
      this._selectActiveItemAtIndex(nextIndex)
    }
  }
  _handleUpKey() {
    const activeItem = this._getActiveItem()
    if (activeItem === undefined) {
      this._selectActiveItemAtIndex(this.state.autocompleteItems.length - 1)
    } else {
      let prevIndex
      if (activeItem.index === 0) {
        prevIndex = this.state.autocompleteItems.length - 1
      } else {
        prevIndex = (activeItem.index - 1) % this.state.autocompleteItems.length
      }
      this._selectActiveItemAtIndex(prevIndex)
    }
  }
  handleInputKeyDown(event) {
    const ARROW_UP = 38
    const ARROW_DOWN = 40
    const ENTER_KEY = 13
    const ESC = 27
    switch (event.keyCode) {
      case ENTER_KEY:
        event.preventDefault()
        this._handleEnterKey()
        break
      case ARROW_DOWN:
        this._handleDownKey()
        break
      case ARROW_UP:
        this._handleUpKey()
        break
      case ESC:
        this.clearAutocomplete()
        break
    }
  }
  _setActiveItemAtIndex(index) {
    this.setState({
      autocompleteItems: this.state.autocompleteItems.map((item, idx) => {
        if (idx === index) {
          return { ...item, active: true }
        }
        return { ...item, active: false }
      })
    })
  }
  handleInputChange(event) {
    const value = event.target.value && { name: event.target.value }
    this.props.onChange(value)
    if (!event.target.value) {
      this.clearAutocomplete()
      return
    }
    if (event.target.value && event.target.value.length > 2) {
      this.getSuggestionsDebounced(event.target.value)
    }
  }
  renderOverlay() {
    if (this.state.autocompleteItems.length === 0) {
      return null
    }
    return <div style={styles.autocompleteOverlay} onClick={() => this.clearAutocomplete()} />
  }
  renderAutocomplete() {
    const { autocompleteItems } = this.state
    if (autocompleteItems.length === 0) {
      return null
    }
    return (
      <div data-test="suggestions" style={styles.autocompleteWrapper}>
        {autocompleteItems.map((place, index) => (
          <div
            key={place.placeId}
            onClick={() => this.selectAddress(place.suggestion, place.placeId)}
            style={[styles.autocompleteItem, place.active ? { background: '#dddddd' } : null]}>
            {place.suggestion}
          </div>
        ))}
      </div>
    )
  }
  render() {
    const {
      placeholder, value, onFocus, onBlur, isValid, message, styleName
    } = this.props
    return (
      <div data-test="postcode-suggest" style={styles.base}>
        <div style={{ position: 'relative' }}>
          <InputText
            onFocus={() => {
              if (value && (value.postcode || value.name)) {
                this.getSuggestionsDebounced(value.postcode || value.name)
              }
              onFocus && onFocus()
            }}
            onBlur={onBlur}
            styleName={styleName || 'light'}
            isValid={isValid}
            message={message}
            autoComplete="nope"
            required={this.props.required}
            label={placeholder}
            icon="search"
            value={value && value.name}
            onChange={this.handleInputChange}
            onKeyDown={this.handleInputKeyDown} />
          {this.renderOverlay()}
          {this.renderAutocomplete()}
        </div>
      </div>
    )
  }
}

PostcodeSuggest.propTypes = {
  componentRestrictions: PropTypes.object,
  onChange: PropTypes.func,
  onLocation: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  message: PropTypes.string,
  isValid: PropTypes.bool,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  gtmEvents: PropTypes.array
}

const styles = {
  base: {
    position: 'relative',
    padding: '0',
    margin: 'auto',
    '@media (min-width: 768px)': {
      padding: '0'
    }
  },
  autocompleteOverlay: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 9998
  },
  autocompleteWrapper: {
    position: 'absolute',
    top: 'calc(100% - (0em + 2px))',
    left: '-1em',
    backgroundColor: 'white',
    textAlign: 'left',
    border: '1px solid #ddd',
    boxShadow: '2px 2px 10px rgba(0, 0, 0, .125)',
    width: 'calc(100% + 1em)',
    zIndex: 9999
  },
  autocompleteItem: {
    padding: '1em',
    color: '#191919',
    cursor: 'pointer',
    borderBottom: '1px solid #ddd',
    background: 'white',
    ':hover': {
      background: '#dddddd'
    }
  }
}

export default Radium(PostcodeSuggest)



// WEBPACK FOOTER //
// ./src/components/form-elements/postcode-suggest/index.jsx